// src/components/ui/PictorialChoiceList.tsx
// The `pictorial-choice` field type — a single-answer question whose options
// are better recognised than read (Physical Activity in the mockup; equally
// a pain-face scale, a mobility level). Pre-Consult redesign Phase 3. See
// docs/HMS_V4_ASSESSMENT_FIELD_TYPES_SPEC.md §3.
//
// Stored answer is the selected option's `value` — a plain string, identical
// to `radio`. A vertical `radiogroup` of full-width cards: illustration,
// label, sub-label, radio dot. Every option's icon carries its own colour
// regardless of selection (so the scale reads at a glance); only the
// selected card gets the tonal border/fill and the filled, checked dot —
// colour is a second channel, never the only one, same principle the
// Phase 0 illustration set itself establishes.
import { cn } from '@/lib/cn';
import { COLOR_TONE, TONE_BORDER_BG, TONE_SOLID, renderChoiceIllustration } from './choiceIllustration';
import type { PictorialOption } from '@/lib/assessmentTemplate';

export interface PictorialChoiceListProps {
  options: PictorialOption[];
  value: string | undefined;
  onChange: (value: string) => void;
  /** See SegmentedControl's own doc comment — a selected card must stay fully
   * legible inside a disabled `<fieldset>` (StageEditModal's read-only view). */
  disabled?: boolean;
}

export function PictorialChoiceList({ options, value, onChange, disabled }: PictorialChoiceListProps) {
  return (
    <div role="radiogroup" className="flex flex-col gap-2">
      {options.map(opt => {
        const selected = value === opt.value;
        // A missing colour falls back to 'warn' — same deliberately neutral
        // middle ChoiceMatrix's own cellTone() uses rather than guessing.
        const tone = COLOR_TONE[opt.color ?? 'yellow'];
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={opt.sublabel ? `${opt.label}, ${opt.sublabel}` : opt.label}
            disabled={disabled}
            onClick={() => onChange(opt.value)}
            className={cn(
              'flex items-center gap-3 rounded-[var(--radius)] border px-3 py-2.5 text-left',
              selected ? TONE_BORDER_BG[tone] : 'border-line',
              disabled && 'pointer-events-none disabled:opacity-100'
            )}
          >
            {renderChoiceIllustration(opt.illustration, tone, 'md')}
            <span className="min-w-0 flex-1">
              <span className="block text-[12.5px] font-semibold text-ink">{opt.label}</span>
              {opt.sublabel && <span className="block text-[10.5px] text-ink-muted">{opt.sublabel}</span>}
            </span>
            <span
              aria-hidden="true"
              className={cn(
                'flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-full border-[1.5px] text-[10px] leading-none',
                selected ? cn(TONE_SOLID[tone], 'border-transparent') : 'border-control-line'
              )}
            >
              {selected && '✓'}
            </span>
          </button>
        );
      })}
    </div>
  );
}
